import React, { useEffect, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import Canvas from "./Canvas";
import Filmstrip from "./Filmstrip";
import PropertiesPanel from "./PropertiesPanel";
import defaultImage from "./defaultimage.json";
import { DEFAULT_PANEL_DURATION } from "../../utils/storyboardIO";
import styles from "./StoryboardEditor.module.css";

export default function StoryboardEditor({ storyboard, setStoryboard }) {
  const [currentPageId, setCurrentPageId] = useState(storyboard[0]?.id);
  const [selectedPanelId, setSelectedPanelId] = useState(
    storyboard[0]?.panels?.[0]?.id
  );

  const currentIndex = storyboard.findIndex((p) => p.id === currentPageId);
  const pageData = storyboard[currentIndex];

  // Si la página activa desaparece (eliminada / proyecto importado), cae a
  // la primera disponible.
  useEffect(() => {
    if (!storyboard.some((p) => p.id === currentPageId)) {
      setCurrentPageId(storyboard[0]?.id);
    }
  }, [storyboard, currentPageId]);

  // Si el frame seleccionado ya no existe en la página activa, selecciona
  // el primero de la página.
  useEffect(() => {
    if (!pageData) return;
    if (!pageData.panels.some((p) => p.id === selectedPanelId)) {
      setSelectedPanelId(pageData.panels[0]?.id);
    }
  }, [pageData, selectedPanelId]);

  const selectedPanel = pageData?.panels.find((p) => p.id === selectedPanelId);
  const selectedPanelIndex =
    pageData?.panels.findIndex((p) => p.id === selectedPanelId) ?? -1;

  // ---- Página ----
  const updatePageField = (field, value) => {
    setStoryboard((prev) =>
      prev.map((page) =>
        page.id === currentPageId ? { ...page, [field]: value } : page
      )
    );
  };

  const addPage = () => {
    const newPage = {
      id: `page-${Date.now()}`,
      page: storyboard.length + 1,
      title: `Capítulo ${storyboard.length + 1}`,
      description: "Nueva página agregada.",
      panels: [
        {
          id: Date.now().toString(),
          image: defaultImage.baseImage,
          text: "Nuevo cuadro en página nueva.",
          duration: DEFAULT_PANEL_DURATION,
        },
      ],
    };
    setStoryboard((prev) => [...prev, newPage]);
    setCurrentPageId(newPage.id);
  };

  const deletePage = (pageId) => {
    if (storyboard.length <= 1) {
      alert("Debe quedar al menos una página en el storyboard.");
      return;
    }
    const page = storyboard.find((p) => p.id === pageId);
    if (
      !window.confirm(
        `¿Eliminar "${page?.title || "esta página"}"? No se puede deshacer.`
      )
    )
      return;

    setStoryboard((prev) =>
      prev
        .filter((p) => p.id !== pageId)
        .map((p, index) => ({ ...p, page: index + 1 }))
    );
  };

  const goToIndex = (index) => {
    if (index >= 0 && index < storyboard.length) {
      setCurrentPageId(storyboard[index].id);
    }
  };

  const handlePageDragEnd = (activeId, overId) => {
    if (!overId || activeId === overId) return;
    setStoryboard((prev) => {
      const oldIndex = prev.findIndex((p) => p.id === activeId);
      const newIndex = prev.findIndex((p) => p.id === overId);
      return arrayMove(prev, oldIndex, newIndex).map((p, index) => ({
        ...p,
        page: index + 1,
      }));
    });
  };

  // ---- Frames ----
  const updatePanelText = (panelId, value) => {
    setStoryboard((prev) =>
      prev.map((page) =>
        page.id === currentPageId
          ? {
              ...page,
              panels: page.panels.map((p) =>
                p.id === panelId ? { ...p, text: value } : p
              ),
            }
          : page
      )
    );
  };

  const updatePanelDuration = (panelId, value) => {
    const duration = Math.max(0.1, Number(value) || DEFAULT_PANEL_DURATION);
    setStoryboard((prev) =>
      prev.map((page) =>
        page.id === currentPageId
          ? {
              ...page,
              panels: page.panels.map((p) =>
                p.id === panelId ? { ...p, duration } : p
              ),
            }
          : page
      )
    );
  };

  const updatePanelImage = (panelId, imageBase64) => {
    setStoryboard((prev) =>
      prev.map((page) =>
        page.id === currentPageId
          ? {
              ...page,
              panels: page.panels.map((p) =>
                p.id === panelId ? { ...p, image: imageBase64 } : p
              ),
            }
          : page
      )
    );
  };

  const addPanel = () => {
    const newPanel = {
      id: Date.now().toString(),
      image: defaultImage.baseImage,
      text: "Nuevo cuadro agregado.",
      duration: DEFAULT_PANEL_DURATION,
    };
    setStoryboard((prev) =>
      prev.map((page) =>
        page.id === currentPageId
          ? { ...page, panels: [...page.panels, newPanel] }
          : page
      )
    );
    setSelectedPanelId(newPanel.id);
  };

  const duplicatePanel = (panelId) => {
    const copyId = `${Date.now()}-copy`;
    setStoryboard((prev) =>
      prev.map((page) => {
        if (page.id !== currentPageId) return page;
        const index = page.panels.findIndex((p) => p.id === panelId);
        if (index === -1) return page;
        const copy = { ...page.panels[index], id: copyId };
        const panels = [...page.panels];
        panels.splice(index + 1, 0, copy);
        return { ...page, panels };
      })
    );
    setSelectedPanelId(copyId);
  };

  const deletePanel = (panelId) => {
    if (!pageData || pageData.panels.length <= 1) {
      alert("La página debe tener al menos un frame.");
      return;
    }
    if (!window.confirm("¿Eliminar este frame? No se puede deshacer.")) return;

    setStoryboard((prev) =>
      prev.map((page) =>
        page.id === currentPageId
          ? { ...page, panels: page.panels.filter((p) => p.id !== panelId) }
          : page
      )
    );
  };

  const handlePanelDragEnd = (activeId, overId) => {
    if (!overId || activeId === overId) return;
    setStoryboard((prev) =>
      prev.map((page) => {
        if (page.id === currentPageId) {
          const oldIndex = page.panels.findIndex((p) => p.id === activeId);
          const newIndex = page.panels.findIndex((p) => p.id === overId);
          return { ...page, panels: arrayMove(page.panels, oldIndex, newIndex) };
        }
        return page;
      })
    );
  };

  if (!pageData) return null;

  return (
    <div className={styles.editor}>
      <div className={styles.center}>
        <Canvas
          page={pageData}
          pageIndex={currentIndex}
          pageCount={storyboard.length}
          panel={selectedPanel}
          panelIndex={selectedPanelIndex}
          panelCount={pageData.panels.length}
          onImageChange={updatePanelImage}
        />
        <Filmstrip
          storyboard={storyboard}
          currentPageId={currentPageId}
          onSelectPage={setCurrentPageId}
          onDeletePage={deletePage}
          onReorderPages={handlePageDragEnd}
          onAddPage={addPage}
          onGoToIndex={goToIndex}
          currentIndex={currentIndex}
          pageData={pageData}
          selectedPanelId={selectedPanelId}
          onSelectPanel={setSelectedPanelId}
          onReorderPanels={handlePanelDragEnd}
          onAddPanel={addPanel}
        />
      </div>

      <PropertiesPanel
        page={pageData}
        onUpdatePageField={updatePageField}
        onDeletePage={deletePage}
        canDeletePage={storyboard.length > 1}
        panel={selectedPanel}
        panelIndex={selectedPanelIndex}
        onUpdatePanelText={updatePanelText}
        onUpdatePanelDuration={updatePanelDuration}
        onDuplicatePanel={duplicatePanel}
        onDeletePanel={deletePanel}
        canDeletePanel={pageData.panels.length > 1}
      />
    </div>
  );
}
